import { motion } from "framer-motion";
import {
  MailWarning,
  ClipboardX,
  Clock3,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  BellRing,
  ShieldCheck,
  FileSignature,
  BookOpenCheck,
  Activity,
} from "lucide-react";
import BrandName from "@/components/site/BrandName";
import Button from "@/components/site/Button";

/* ------------------------------------------------------------------ */
/*  Left column: the chaotic "today" — muted, red flags, missed work  */
/* ------------------------------------------------------------------ */

function PainInboxFragment() {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#080f1e]/70 p-3.5 shadow-inner">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">
          <MailWarning size={11} className="text-rose-400/80" />
          Inbox &middot; policy update
        </div>
        <span className="text-[9px] font-mono text-slate-600">18 recipients</span>
      </div>
      <div className="space-y-1.5">
        {[
          ["Nurse team", "Opened", "rose"],
          ["Dispensing floor", "Not opened", "rose"],
          ["Night shift", "Not opened", "rose"],
          ["Reception", "Opened", "amber"],
        ].map(([who, status, tone]) => (
          <div
            key={who}
            className="flex items-center justify-between rounded bg-white/[0.015] px-2.5 py-1.5"
          >
            <span className="text-[11px] text-slate-400 tracking-tight">
              {who}
            </span>
            <span
              className={`text-[10px] font-mono ${
                tone === "rose" ? "text-rose-300/90" : "text-amber-300/90"
              }`}
            >
              {status}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-2.5 pt-2.5 border-t border-white/[0.05] text-[10px] text-rose-300/80 flex items-center gap-1.5">
        <AlertTriangle size={10} /> Read receipts do not prove comprehension
      </div>
    </div>
  );
}

function PainSpreadsheetFragment() {
  const rows = [
    ["ID-041", "SOP-CB-14", "—", "Missing"],
    ["ID-042", "SOP-CB-14", "22 Mar", "Chased 3x"],
    ["ID-043", "SOP-CB-14", "—", "Missing"],
    ["ID-044", "SOP-CB-14", "17 Mar", "Signed"],
    ["ID-045", "SOP-CB-14", "—", "No response"],
  ];
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#080f1e]/70 p-3.5">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">
          <ClipboardX size={11} className="text-rose-400/80" />
          Ack. tracker.xlsx
        </div>
        <span className="text-[9px] font-mono text-slate-600">v14 &middot; edited by 4</span>
      </div>
      <div className="grid grid-cols-[1fr_1.4fr_0.8fr_1fr] gap-x-2 text-[10px] font-mono text-slate-600 pb-1 border-b border-white/[0.05]">
        <span>Staff</span>
        <span>Policy</span>
        <span>Date</span>
        <span>Status</span>
      </div>
      {rows.map((r, i) => (
        <div
          key={i}
          className="grid grid-cols-[1fr_1.4fr_0.8fr_1fr] gap-x-2 text-[10.5px] py-1.5 border-b border-white/[0.03] last:border-b-0"
        >
          <span className="text-slate-400">{r[0]}</span>
          <span className="text-slate-500 truncate">{r[1]}</span>
          <span className="text-slate-500">{r[2]}</span>
          <span
            className={`font-medium ${
              r[3] === "Signed"
                ? "text-emerald-300/70"
                : r[3] === "Missing" || r[3] === "No response"
                ? "text-rose-300/90"
                : "text-amber-300/90"
            }`}
          >
            {r[3]}
          </span>
        </div>
      ))}
    </div>
  );
}

function PainReminderStack() {
  return (
    <div className="relative">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            transform: `translate(${i * 6}px, ${i * -3}px) rotate(${
              (i - 1) * 1.2
            }deg)`,
            zIndex: 3 - i,
          }}
          className="absolute inset-x-0 top-0 rounded-lg border border-white/[0.06] bg-[#080f1e]/80 px-3.5 py-3 shadow-lg"
        >
          <div className="flex items-center gap-2">
            <BellRing size={12} className="text-rose-400/80" />
            <span className="text-[11px] text-slate-300">
              Reminder: SOP-CB-14 outstanding
            </span>
            <span className="ml-auto text-[9.5px] font-mono text-rose-300/90">
              overdue &middot; {14 - i * 3}d
            </span>
          </div>
        </div>
      ))}
      <div className="h-[52px]" />
    </div>
  );
}

function PainRegChatter() {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#080f1e]/70 p-3.5">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">
          <Clock3 size={11} className="text-amber-400/80" />
          Regulator update &middot; unread
        </div>
        <span className="text-[9px] font-mono text-amber-300/80">3 weeks old</span>
      </div>
      <p className="text-[11.5px] text-slate-500 leading-relaxed line-clamp-2">
        &ldquo;Amendment to storage &amp; handling schedule 2. Operators must
        evidence updated training within 30 days of publication...&rdquo;
      </p>
      <div className="mt-2 flex items-center gap-1.5 text-[10px] text-rose-300/80">
        <AlertTriangle size={10} /> Not yet distributed to staff
      </div>
    </div>
  );
}

function PainAuditGap() {
  return (
    <div className="rounded-lg border border-rose-500/25 bg-rose-500/[0.04] p-3.5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-rose-300/90 font-mono">
          <AlertTriangle size={11} />
          Inspection in 9 days
        </div>
        <span className="text-[9px] font-mono text-rose-300/70">Preparation</span>
      </div>
      <div className="text-[12px] text-slate-300">
        Rebuild trail for
        <span className="text-white font-semibold"> 42 staff </span>
        across
        <span className="text-white font-semibold"> 7 policies </span>
        by hand.
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
        <div className="h-full w-[24%] bg-rose-400/70" />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[10px] font-mono">
        <span className="text-slate-500">24% assembled</span>
        <span className="text-rose-300/90">Gaps remain</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Right column: CanniComply — calm dashboard, emerald verified      */
/* ------------------------------------------------------------------ */

function GoodLiveFeed() {
  const events = [
    { icon: FileSignature, tone: "gold", who: "Nurse team", what: "SOP-CB-14 distributed", t: "just now" },
    { icon: CheckCircle2, tone: "emerald", who: "Dr. R. Ahmed", what: "acknowledged \u00b7 comprehension 96%", t: "1m" },
    { icon: BookOpenCheck, tone: "emerald", who: "Reception", what: "training module complete", t: "3m" },
    { icon: ShieldCheck, tone: "emerald", who: "Audit log", what: "event chained \u00b7 #A7F2", t: "3m" },
  ];
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#0A1424]/80 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-mono">
          <Activity size={11} />
          Live compliance feed
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10px] text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Live
        </span>
      </div>
      <ul className="space-y-2">
        {events.map((e, i) => {
          const Icon = e.icon;
          const ring =
            e.tone === "emerald"
              ? "border-emerald-400/30 bg-emerald-500/[0.08] text-emerald-300"
              : "border-[#D4A65A]/30 bg-[#D4A65A]/[0.08] text-[#D4A65A]";
          return (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: 6 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.08 }}
              className="flex items-center gap-3 rounded-lg border border-white/[0.05] bg-white/[0.015] px-3 py-2"
            >
              <span
                className={`h-7 w-7 shrink-0 rounded-md border ${ring} flex items-center justify-center`}
              >
                <Icon size={12} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] text-slate-200">
                  <span className="text-white font-semibold">{e.who}</span>{" "}
                  {e.what}
                </div>
              </div>
              <span className="text-[10px] font-mono text-slate-500 shrink-0">
                {e.t}
              </span>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}

function GoodMetricsGrid() {
  const metrics = [
    { label: "Acknowledgement", value: 98, target: "Target 95" },
    { label: "Comprehension", value: 92, target: "Avg score" },
    { label: "Training complete", value: 100, target: "Q1 cohort" },
  ];
  return (
    <div className="grid grid-cols-3 gap-2">
      {metrics.map((m) => (
        <div
          key={m.label}
          className="rounded-lg border border-white/[0.06] bg-[#0A1424]/80 p-3"
        >
          <div className="text-[9.5px] uppercase tracking-[0.18em] text-slate-500 font-mono">
            {m.label}
          </div>
          <div className="mt-1.5 flex items-baseline gap-1">
            <span className="text-white text-[22px] font-semibold tracking-tight">
              {m.value}
            </span>
            <span className="text-emerald-300 text-[11px] font-mono">%</span>
          </div>
          <div className="mt-2 h-1 rounded-full bg-white/[0.05] overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-400/80 to-emerald-300"
              style={{ width: `${m.value}%` }}
            />
          </div>
          <div className="mt-1.5 text-[9.5px] text-slate-500 font-mono">
            {m.target}
          </div>
        </div>
      ))}
    </div>
  );
}

function GoodRegDigest() {
  return (
    <div className="rounded-xl border border-[#D4A65A]/20 bg-gradient-to-br from-[#0F1A2E]/80 to-[#0A1424]/80 p-4">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-mono">
          <BookOpenCheck size={11} />
          Legislative digest
        </div>
        <span className="text-[10px] text-emerald-300 inline-flex items-center gap-1">
          <CheckCircle2 size={10} />
          Distributed
        </span>
      </div>
      <p className="text-[12px] text-slate-300 leading-relaxed">
        Schedule 2 handling amendment
        <span className="text-slate-500"> &middot; </span>
        <span className="text-white font-semibold">42 of 42 staff</span> received
        the plain English summary in 4 minutes.
      </p>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        {[
          ["Summarised", "AI drafted"],
          ["Assigned", "By role"],
          ["Trained", "Adaptive"],
        ].map(([a, b]) => (
          <div
            key={a}
            className="rounded-md border border-white/[0.05] bg-white/[0.015] py-1.5"
          >
            <div className="text-[10.5px] text-white font-semibold">{a}</div>
            <div className="text-[9.5px] font-mono text-slate-500 mt-0.5">
              {b}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GoodInspectionBadge() {
  return (
    <div className="rounded-xl border border-emerald-400/25 bg-emerald-500/[0.04] p-4 relative overflow-hidden">
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-emerald-500/[0.12] blur-3xl" />
      <div className="relative flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 rounded-lg border border-emerald-400/40 bg-emerald-500/[0.1] flex items-center justify-center">
          <ShieldCheck size={16} className="text-emerald-300" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/90 font-mono">
            Inspection ready
          </div>
          <div className="mt-1 text-white text-[14px] font-semibold tracking-tight">
            Every event chained &middot; retained 7 years
          </div>
          <div className="mt-2 flex items-center gap-3 text-[10.5px] font-mono">
            <span className="text-slate-400">SHA-256</span>
            <span className="text-slate-600">|</span>
            <span className="text-slate-400">Signed</span>
            <span className="text-slate-600">|</span>
            <span className="text-slate-400">QR verifiable</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Pain / gain narrative rows                                        */
/* ------------------------------------------------------------------ */

const painRows = [
  {
    tag: "Silent inboxes",
    pain: "Policies get buried. Read receipts prove nothing.",
    gain: "Every distribution tracked, understood and signed.",
  },
  {
    tag: "Manual chasing",
    pain: "Managers rebuild trackers in spreadsheets every week.",
    gain: "Acknowledgement, training and comprehension automated.",
  },
  {
    tag: "Legislative lag",
    pain: "Regulator updates sit in a folder no one reads.",
    gain: "New rules summarised, distributed and trained on in minutes.",
  },
  {
    tag: "Audit panic",
    pain: "Preparing for inspection becomes a two-week project.",
    gain: "The audit is already assembled. Nothing to rebuild.",
  },
];

/* ------------------------------------------------------------------ */
/*  Section                                                           */
/* ------------------------------------------------------------------ */

export default function PainPoints() {
  return (
    <section
      id="where-it-helps"
      data-testid="pain-points-section"
      className="relative py-14 md:py-28 bg-gradient-to-b from-[#050b1a] to-[#040914] border-y border-white/[0.04] overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none grid-lines opacity-20" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="eyebrow">Recognise this?</div>
            <h2 className="mt-3 font-display text-4xl md:text-[44px] tracking-[-0.035em] text-white leading-[1.02] font-semibold">
              The daily compliance chaos.<br />
              <span className="text-[#D4A65A]">
                Quietly removed.
              </span>
            </h2>
          </div>
          <p className="max-w-sm text-slate-400 leading-relaxed text-[15px]">
            Every regulated operator we speak to lives the same four
            frustrations. <BrandName /> takes each one off your team and
            replaces it with permanent, inspection ready evidence.
          </p>
        </div>

        {/* Comparison canvas */}
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {/* LEFT: Chaos */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55 }}
            className="col-span-12 lg:col-span-6 relative rounded-2xl border border-white/[0.05] bg-gradient-to-b from-[#070d1c] to-[#040914] p-5 md:p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
                Today &middot; without <BrandName />
              </div>
              <span className="text-[9.5px] font-mono text-slate-600">
                Mon 09:14 &middot; Managers desk
              </span>
            </div>

            <div className="grid grid-cols-6 gap-3 [filter:saturate(0.6)]">
              <div className="col-span-6 md:col-span-3">
                <PainInboxFragment />
              </div>
              <div className="col-span-6 md:col-span-3">
                <PainSpreadsheetFragment />
              </div>
              <div className="col-span-6 md:col-span-3">
                <PainReminderStack />
              </div>
              <div className="col-span-6 md:col-span-3">
                <PainRegChatter />
              </div>
              <div className="col-span-6">
                <PainAuditGap />
              </div>
            </div>

            {/* Divider label between panels on wide screens */}
            <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10">
              <div className="h-10 w-10 rounded-full border border-white/10 bg-[#050b18] flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(212,166,90,0.4)]">
                <ArrowRight size={14} className="text-[#D4A65A]" />
              </div>
            </div>
          </motion.div>

          {/* RIGHT: Calm */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="col-span-12 lg:col-span-6 relative rounded-2xl border border-[#D4A65A]/20 bg-gradient-to-br from-[#0C1730] via-[#0A1424] to-[#050B18] p-5 md:p-6"
          >
            <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-emerald-500/[0.12] blur-3xl" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-mono">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D4A65A]" />
                  With <BrandName />
                </div>
                <span className="text-[9.5px] font-mono text-slate-500">
                  Mon 09:14 &middot; Compliance dashboard
                </span>
              </div>

              <div className="space-y-3">
                <GoodMetricsGrid />
                <GoodLiveFeed />
                <div className="grid md:grid-cols-2 gap-3">
                  <GoodRegDigest />
                  <GoodInspectionBadge />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Narrative rows below the visual proof */}
        <div className="mt-14 grid gap-px bg-white/[0.05] rounded-2xl overflow-hidden border border-white/[0.06]">
          <div className="grid grid-cols-12 gap-px bg-white/[0.05]">
            <div className="col-span-12 md:col-span-3 bg-[#050b18] px-5 py-3 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono">
              Frustration
            </div>
            <div className="col-span-12 md:col-span-4 bg-[#050b18] px-5 py-3 text-[10px] uppercase tracking-[0.22em] text-rose-300/80 font-mono">
              Without <BrandName />
            </div>
            <div className="col-span-12 md:col-span-5 bg-[#050b18] px-5 py-3 text-[10px] uppercase tracking-[0.22em] text-emerald-300/90 font-mono">
              With <BrandName />
            </div>
          </div>
          {painRows.map((r, i) => (
            <motion.div
              key={r.tag}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="grid grid-cols-12 gap-px bg-white/[0.04]"
            >
              <div className="col-span-12 md:col-span-3 bg-[#070d1c] px-5 py-4">
                <div className="text-white text-[13.5px] font-semibold tracking-tight">
                  {r.tag}
                </div>
              </div>
              <div className="col-span-12 md:col-span-4 bg-[#070d1c] px-5 py-4">
                <div className="flex items-start gap-2 text-[13px] text-slate-400 leading-relaxed">
                  <AlertTriangle
                    size={13}
                    className="mt-0.5 text-rose-400/80 shrink-0"
                  />
                  {r.pain}
                </div>
              </div>
              <div className="col-span-12 md:col-span-5 bg-[#0A1424] px-5 py-4">
                <div className="flex items-start gap-2 text-[13px] text-slate-200 leading-relaxed">
                  <CheckCircle2
                    size={13}
                    className="mt-0.5 text-emerald-300 shrink-0"
                  />
                  {r.gain}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Closing bar */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[13px] text-slate-400 max-w-xl">
            The point is not more software. The point is less firefighting,
            calmer teams and permanent evidence sitting where an inspector can
            already find it.
          </p>
          <Button
            as="a"
            href="#process"
            data-testid="painpoints-see-journey"
            variant="primary"
            size="sm"
          >
            See the compliance journey
            <ArrowRight size={12} />
          </Button>
        </div>
      </div>
    </section>
  );
}
